import "./redeem-reward-dialog.scss";

import { MessageResponseDto } from "@api/api";
import { Constraint } from "@core/model/constraint.enum";
import { EarnPreference } from "@core/model/earn-preference.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import GridItem from "@core/ui/grid-item/grid-item";
import { useQyGetBranch } from "@factory/query/branch.query";
import { useQyRedeemReward } from "@factory/query/reward.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { LabelValue } from "@shared/models/label-value.interface";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import Scan from "@shared/ui/scan/scan";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSphereRedeemItemCtx } from "../sphere-redeem-item/sphere-redeem-item.context";

export interface RedeemRewardDialogProps {}

export function RedeemRewardDialog() {
  // DECLARATIONS
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();
  const { selectedRedeemCampaign, cardAlias, setCardAlias } =
    useSphereRedeemItemCtx();
  const navigate = useNavigate();
  const [scan, setScan] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branchOption, seBranchOption] = useState<LabelValue[]>([]);
  const [preference, setPreference] = useState(EarnPreference.POINTS);
  const { mapRedeemReward } = useFormApiMapper();
  const { showSuccess } = useNotificationContext();

  // API DEFINITIONS
  const handleRedeemSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`Redemption complete`);
    navigate(
      `success?points=${data.points}&stamp=${data.stamp}&return=campaign`
    );
  };
  const { mutate: redeem } = useQyRedeemReward(handleRedeemSuccess);

  const handleSuccess = () => {
    const responseData = response?.data || [];
    if (responseData.length === 0) {
      return;
    }

    setSelectedBranch(responseData?.[0].code);
    const _branchOption = LabelValue.MapArray(
      response?.data || [],
      (item) => item.name,
      (item) => item.code
    );
    seBranchOption(_branchOption);
  };
  const { data: response } = useQyGetBranch(
    "",
    9999,
    0,
    undefined,
    undefined,
    true,
    handleSuccess
  );

  // LOCAL FUNCTIONS
  const handleSuccessScan = (value: string) => {
    setCardAlias(value);
  };
  const handleCloseScan = () => {
    setScan(false);
  };
  const handleRedeem = () => {
    if (!cardAlias || !selectedRedeemCampaign?.code) {
      return;
    }

    const value = mapRedeemReward(
      cardAlias,
      selectedRedeemCampaign.code,
      selectedBranch,
      tenant,
      preference,
      undefined,
      undefined
    );
    redeem(value);
  };

  // LOCAL RENDERS
  const footerContent = (
    <div>
      <Button
        className="block w-full mt-2"
        label="Redeem"
        onClick={() => handleRedeem()}
      />
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => navigate("/business/campaign")}
      />
    </div>
  );
  const radioGroup = (
    <section>
      <p>Select Redemption Preference</p>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex align-items-center">
          <RadioButton
            inputId="preference-point"
            name="preference"
            value={EarnPreference.POINTS}
            onChange={(e) => setPreference(e.value)}
            checked={preference === EarnPreference.POINTS}
          />
          <label htmlFor="preference-point" className="ml-2">
            {EarnPreference.POINTS}
          </label>
        </div>

        <div className="flex align-items-center">
          <RadioButton
            inputId="preference-stamp"
            name="preference"
            value={EarnPreference.STAMP}
            onChange={(e) => setPreference(e.value)}
            checked={preference === EarnPreference.STAMP}
          />
          <label htmlFor="preference-stamp" className="ml-2">
            {EarnPreference.STAMP}
          </label>
        </div>
      </div>
    </section>
  );

  return (
    <div id="RedeemRewardDialog" className="redeem-reward-dialog">
      <Dialog
        header={
          selectedRedeemCampaign?.name
            ? `Redeem ${selectedRedeemCampaign?.name}`
            : "Redeem"
        }
        visible={true}
        style={{ width: "50vw" }}
        onHide={() => {
          navigate("/business/campaign");
        }}
        closeOnEscape={true}
        footer={footerContent}
        closable={false}
      >
        <div className="m-0">
          {scan ? (
            <Scan
              successScan={handleSuccessScan}
              closeScan={handleCloseScan}
              isCloseOnScan={true}
            />
          ) : null}
          <div className="mt-6"></div>
          <div className="mb-6">
            <div className="p-inputgroup flex-1">
              <FloatLabel>
                <InputText
                  id="userCode"
                  value={cardAlias}
                  onChange={(e) => setCardAlias(e.target.value)}
                  keyfilter="int"
                  className="text-4xl w-full"
                  autoFocus
                  tabIndex={0}
                />
                <label htmlFor="amount">Enter User Code</label>
              </FloatLabel>

              <span
                className="p-inputgroup-addon cursor-pointer px-8 bg-emerald-500 border border-emerald-400"
                onClick={() => setScan(true)}
              >
                <i className="pi pi-camera text-white"></i>
              </span>
            </div>
          </div>
          {selectedRedeemCampaign?.constraint === Constraint.OR
            ? radioGroup
            : null}

          {selectedRedeemCampaign ? (
            <GridItem campaign={selectedRedeemCampaign} />
          ) : null}

          <Dropdown
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.value)}
            options={branchOption}
            placeholder="Select a branch"
            className="w-full md:w-14rem mt-6 text-4xl"
          />
        </div>
      </Dialog>
    </div>
  );
}

export default RedeemRewardDialog;
